/**
 * InvoicesPage — Phase 12 Connected Invoices list + create + detail.
 *
 * Routes:
 *   /invoices            — list with All/Customer/Supplier tabs + filters
 *   /invoices/new        — create form
 *   /invoices/:id        — detail (with Approve/Reject/Payment/PDF actions)
 *
 * Companion to `BankPage` from Phase 10 — same SOM-TURK visual language.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import BackToDashBar from "@/components/BackToDashBar";
import {
  Card, CardContent, CardHeader, CardTitle,
} from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Tabs, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  FileText, Plus, Loader2, Trash2, ExternalLink, Download, Send,
  CheckCircle2, XCircle, Banknote, Copy, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { previewPDF, downloadPDF } from "@/lib/pdf";
import { useAuth } from "@/context/AuthContext";
import { ROLES } from "@/lib/permissions";
import { fmtUSD } from "@/lib/tx";

const WRITE_ROLES = new Set([
  ROLES.SYSTEM_ADMIN, ROLES.SALIHI_ACCOUNTANT,
  ROLES.SALIHI_SENIOR_ACCOUNTANT, ROLES.SALIHI_FINANCE_MANAGER,
]);
const APPROVE_ROLES = new Set([
  ROLES.SYSTEM_ADMIN, ROLES.SALIHI_FINANCE_MANAGER,
  ROLES.SALIHI_SENIOR_ACCOUNTANT,
]);

const PAY_STATUS_STYLE = {
  paid: "bg-emerald-100 text-emerald-700 border-emerald-200",
  partially_paid: "bg-amber-100 text-amber-800 border-amber-200",
  unpaid: "bg-slate-100 text-slate-700 border-slate-200",
  overdue: "bg-red-100 text-red-700 border-red-200",
  cancelled: "bg-slate-200 text-slate-500 border-slate-300 line-through",
};
const INV_STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-700",
  pending_approval: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700",
  cancelled: "bg-slate-200 text-slate-500 line-through",
  sent: "bg-sky-100 text-sky-700",
};

function pdfPath(id) {
  return `/invoices/${id}/pdf`;
}

async function handlePreview(inv) {
  try {
    await previewPDF(pdfPath(inv.id));
  } catch (e) {
    toast.error(`PDF preview failed: ${e.message || e}`);
  }
}

async function handleDownload(inv) {
  try {
    await downloadPDF(pdfPath(inv.id), `${inv.invoice_number || inv.id}.pdf`);
  } catch (e) {
    toast.error(`PDF download failed: ${e.message || e}`);
  }
}

export default function InvoicesPage() {
  const [tab, setTab] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const { user, can } = useAuth();
  const navigate = useNavigate();
  // Phase 19 PR-2 hot-fix (2026-07-06) — permission-key gate.
  const canCreate = can ? can("invoices.connected.create") : false;
  const canWrite = canCreate || (can ? can("invoices.connected.edit") : false);
  // P0 Bug fix (2026-07-10) — Actions-column delete gating.
  const canDelete = can ? can("invoices.connected.delete") : false;
  const canDeletePosted = can ? can("invoices.connected.delete_posted") : false;
  const canEdit = can ? can("invoices.connected.edit") : false;
  const canEditPosted = can ? can("invoices.connected.edit_posted") : false;
  const [deleting, setDeleting] = useState(null); // invoice obj currently being deleted
  const [deleteReason, setDeleteReason] = useState("");

  const POSTED_STATES = new Set(["approved", "sent"]);

  async function confirmDelete() {
    if (!deleting) return;
    const isPosted = POSTED_STATES.has(deleting.invoice_status);
    if (isPosted && deleteReason.trim().length < 5) {
      toast.error("Reason is required (min 5 chars) when deleting a posted invoice.");
      return;
    }
    try {
      await api.delete(`/invoices/${deleting.id}`, {
        data: { reason: deleteReason.trim() || undefined },
      });
      toast.success(`Invoice ${deleting.invoice_number || ""} deleted.`);
      setDeleting(null);
      setDeleteReason("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (tab !== "all") params.invoice_type = tab;
      if (statusFilter !== "all") params.invoice_status = statusFilter;
      if (paymentFilter !== "all") params.payment_status = paymentFilter;
      const { data } = await api.get("/invoices", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [tab, statusFilter, paymentFilter]); // eslint-disable-line

  return (
    <div className="space-y-5" data-testid="invoices-page">
      <BackToDashBar />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold">Connected Invoices</h2>
          <p className="text-sm text-slate-500 mt-1">
            Itemized invoices that post to AR/AP and the General Ledger.
          </p>
        </div>
        {canCreate && (
          <Button
            onClick={() => navigate("/invoices/new")}
            className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white"
            data-testid="invoice-create-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> New Connected Invoice
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="invoice-tabs">
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="customer" data-testid="invoice-tab-customer">Customer</TabsTrigger>
          <TabsTrigger value="supplier" data-testid="invoice-tab-supplier">Supplier</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card>
        <CardContent className="p-4 grid gap-3 md:grid-cols-3">
          <div>
            <Label>Invoice status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="invoice-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["all","draft","pending_approval","approved","rejected","cancelled","sent"].map((v) => (
                  <SelectItem key={v} value={v}>{v.replace("_"," ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Payment status</Label>
            <Select value={paymentFilter} onValueChange={setPaymentFilter}>
              <SelectTrigger data-testid="invoice-filter-payment"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["all","unpaid","partially_paid","paid","overdue","cancelled"].map((v) => (
                  <SelectItem key={v} value={v}>{v.replace("_"," ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="animate-spin h-6 w-6 text-[#4FA3D1]" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              <FileText className="h-10 w-10 mx-auto mb-3 text-slate-300" />
              <p>No invoices yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="invoice-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Party</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Due</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((inv) => {
                    const isPosted = POSTED_STATES.has(inv.invoice_status);
                    const showEdit = canEdit && (!isPosted || canEditPosted);
                    const showDelete = canDelete && (!isPosted || canDeletePosted);
                    return (
                    <TableRow
                      key={inv.id}
                      className="cursor-pointer hover:bg-slate-50"
                      data-testid={`invoice-row-${inv.id}`}
                    >
                      <TableCell onClick={() => navigate(`/invoices/${inv.id}`)} className="font-mono text-sm">{inv.invoice_number}</TableCell>
                      <TableCell onClick={() => navigate(`/invoices/${inv.id}`)}>
                        <Badge variant="outline">{inv.invoice_type}</Badge>
                      </TableCell>
                      <TableCell onClick={() => navigate(`/invoices/${inv.id}`)} className="font-medium">{inv.party_name}</TableCell>
                      <TableCell onClick={() => navigate(`/invoices/${inv.id}`)}>{inv.invoice_date}</TableCell>
                      <TableCell onClick={() => navigate(`/invoices/${inv.id}`)}>{inv.due_date || "—"}</TableCell>
                      <TableCell onClick={() => navigate(`/invoices/${inv.id}`)} className="text-right tabular-nums">{fmtUSD(inv.total_amount)}</TableCell>
                      <TableCell onClick={() => navigate(`/invoices/${inv.id}`)} className="text-right tabular-nums font-semibold">
                        {fmtUSD(inv.balance_due)}
                      </TableCell>
                      <TableCell onClick={() => navigate(`/invoices/${inv.id}`)}>
                        <Badge className={PAY_STATUS_STYLE[inv.payment_status]}>
                          {(inv.payment_status || "unpaid").replace("_"," ")}
                        </Badge>
                      </TableCell>
                      <TableCell onClick={() => navigate(`/invoices/${inv.id}`)}>
                        <Badge className={INV_STATUS_STYLE[inv.invoice_status]}>
                          {(inv.invoice_status || "draft").replace("_"," ")}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            title="View"
                            data-testid={`invoice-view-${inv.id}`}
                            onClick={() => navigate(`/invoices/${inv.id}`)}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                          {showEdit && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              title="Edit"
                              data-testid={`invoice-edit-${inv.id}`}
                              onClick={() => navigate(`/invoices/${inv.id}`)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                          {showDelete && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Delete"
                              data-testid={`invoice-delete-${inv.id}`}
                              onClick={() => { setDeleting(inv); setDeleteReason(""); }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* P0 Bug fix (2026-07-10) — invoice delete confirmation dialog. */}
      <Dialog open={!!deleting} onOpenChange={(o) => { if (!o) { setDeleting(null); setDeleteReason(""); } }}>
        <DialogContent data-testid="invoice-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete invoice {deleting?.invoice_number}?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-slate-700">
              Party: <span className="font-medium">{deleting?.party_name}</span><br />
              Total: <span className="font-mono">{deleting && fmtUSD(deleting.total_amount)}</span><br />
              Status: <span className="font-mono">{deleting?.invoice_status}</span>
            </p>
            {deleting && POSTED_STATES.has(deleting.invoice_status) && (
              <>
                <p className="text-amber-700 text-xs">
                  This invoice is posted to the GL. Deleting will reverse its
                  journal entries and remove the linked transaction. This action
                  is audit-logged.
                </p>
                <div>
                  <Label htmlFor="del-reason">Reason (required, min 5 chars)</Label>
                  <Textarea
                    id="del-reason"
                    rows={2}
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                    placeholder="e.g. created in error, posted to wrong customer"
                    data-testid="invoice-delete-reason"
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setDeleting(null); setDeleteReason(""); }}
                    data-testid="invoice-delete-cancel">
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={confirmDelete}
              data-testid="invoice-delete-confirm"
            >
              Delete invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Create / Edit form
// ----------------------------------------------------------------------------

function emptyLine() {
  return { description: "", quantity: 1, unit_price: 0, tax_rate: 0, discount_amount: 0 };
}

export function InvoiceFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [search] = useSearchParams();
  const [form, setForm] = useState({
    invoice_type: search.get("type") || "customer",
    invoice_number: "",
    invoice_date: new Date().toISOString().slice(0, 10),
    due_date: "",
    party_id: "",
    party_name: "",
    party_contact: "",
    notes: "",
    line_items: [emptyLine()],
  });
  const [parties, setParties] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const path = form.invoice_type === "customer" ? "/customers" : "/suppliers";
    api.get(path).then(({ data }) => setParties(data || []));
  }, [form.invoice_type]);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/invoices/${id}`).then(({ data }) => {
      setForm({
        invoice_type: data.invoice_type,
        invoice_number: data.invoice_number,
        invoice_date: data.invoice_date,
        due_date: data.due_date || "",
        party_id: data.party_id || "",
        party_name: data.party_name,
        party_contact: data.party_contact || "",
        notes: data.notes || "",
        line_items: (data.line_items || []).map((l) => ({
          description: l.description, quantity: l.quantity,
          unit_price: l.unit_price, tax_rate: l.tax_rate,
          discount_amount: l.discount_amount,
        })),
      });
    });
  }, [id, isEdit]);

  const totals = useMemo(() => {
    let sub = 0, tax = 0, disc = 0, tot = 0;
    for (const li of form.line_items) {
      const s = Number(li.quantity || 0) * Number(li.unit_price || 0);
      const t = s * Number(li.tax_rate || 0) / 100;
      const d = Number(li.discount_amount || 0);
      sub += s; tax += t; disc += d; tot += s + t - d;
    }
    return { sub, tax, disc, tot };
  }, [form.line_items]);

  const setLine = (i, patch) =>
    setForm((f) => ({ ...f, line_items: f.line_items.map((l, j) => j === i ? { ...l, ...patch } : l) }));

  const save = async () => {
    if (!form.party_name) { toast.error("Pick a party first."); return; }
    if (!form.line_items.length) { toast.error("Add at least one line item."); return; }
    setBusy(true);
    try {
      const payload = { ...form, line_items: form.line_items.filter((l) => l.description && l.quantity > 0) };
      if (isEdit) {
        await api.patch(`/invoices/${id}`, payload);
        toast.success("Invoice updated.");
        navigate(`/invoices/${id}`);
      } else {
        const { data } = await api.post("/invoices", payload);
        toast.success(`Invoice ${data.invoice_number} created.`);
        navigate(`/invoices/${data.id}`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="invoice-form-page">
      <div className="flex items-start justify-between">
        <h2 className="text-2xl font-bold">
          {isEdit ? `Edit invoice ${form.invoice_number}` : "New invoice"}
        </h2>
      </div>

      <Card>
        <CardHeader><CardTitle>Details</CardTitle></CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div>
            <Label>Type</Label>
            <Select
              value={form.invoice_type}
              onValueChange={(v) => setForm((f) => ({ ...f, invoice_type: v, party_id: "", party_name: "" }))}
              disabled={isEdit}
            >
              <SelectTrigger data-testid="inv-form-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="customer">Customer Invoice (AR)</SelectItem>
                <SelectItem value="supplier">Supplier Invoice (AP)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Invoice number</Label>
            <Input
              placeholder="auto-assigned if blank"
              value={form.invoice_number}
              onChange={(e) => setForm((f) => ({ ...f, invoice_number: e.target.value }))}
              data-testid="inv-form-number"
            />
          </div>
          <div>
            <Label>{form.invoice_type === "customer" ? "Customer" : "Supplier"} *</Label>
            <Select
              value={form.party_id}
              onValueChange={(v) => {
                const p = parties.find((x) => x.id === v);
                setForm((f) => ({ ...f, party_id: v, party_name: p?.name || "", party_contact: p?.email || p?.phone || "" }));
              }}
            >
              <SelectTrigger data-testid="inv-form-party"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                {parties.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Link
              to={`/${form.invoice_type === "customer" ? "customers" : "suppliers"}`}
              className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-[#2F86B5] mt-1"
            >
              <ExternalLink className="h-3 w-3" /> Open list
            </Link>
          </div>
          <div>
            <Label>Party contact</Label>
            <Input
              value={form.party_contact}
              onChange={(e) => setForm((f) => ({ ...f, party_contact: e.target.value }))}
              data-testid="inv-form-contact"
            />
          </div>
          <div>
            <Label>Invoice date *</Label>
            <Input
              type="date"
              value={form.invoice_date}
              onChange={(e) => setForm((f) => ({ ...f, invoice_date: e.target.value }))}
              data-testid="inv-form-date"
            />
          </div>
          <div>
            <Label>Due date</Label>
            <Input
              type="date"
              value={form.due_date}
              onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))}
              data-testid="inv-form-due"
            />
          </div>
          <div className="md:col-span-2">
            <Label>Notes</Label>
            <Textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              data-testid="inv-form-notes"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Line items</CardTitle>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setForm((f) => ({ ...f, line_items: [...f.line_items, emptyLine()] }))}
            data-testid="inv-form-add-line"
          >
            <Plus className="h-4 w-4 mr-1" /> Add line
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-20 text-right">Qty</TableHead>
                  <TableHead className="w-28 text-right">Unit Price</TableHead>
                  <TableHead className="w-20 text-right">Tax %</TableHead>
                  <TableHead className="w-24 text-right">Discount</TableHead>
                  <TableHead className="w-28 text-right">Line Total</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {form.line_items.map((l, i) => {
                  const s = Number(l.quantity || 0) * Number(l.unit_price || 0);
                  const t = s * Number(l.tax_rate || 0) / 100;
                  const lineTotal = s + t - Number(l.discount_amount || 0);
                  return (
                    <TableRow key={i} data-testid={`inv-form-line-${i}`}>
                      <TableCell>
                        <Input
                          value={l.description}
                          onChange={(e) => setLine(i, { description: e.target.value })}
                          placeholder="Description"
                          data-testid={`inv-form-line-${i}-desc`}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number" step="0.01" min="0"
                          value={l.quantity}
                          onChange={(e) => setLine(i, { quantity: e.target.value })}
                          className="text-right"
                          data-testid={`inv-form-line-${i}-qty`}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number" step="0.01" min="0"
                          value={l.unit_price}
                          onChange={(e) => setLine(i, { unit_price: e.target.value })}
                          className="text-right"
                          data-testid={`inv-form-line-${i}-price`}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number" step="0.1" min="0" max="100"
                          value={l.tax_rate}
                          onChange={(e) => setLine(i, { tax_rate: e.target.value })}
                          className="text-right"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number" step="0.01" min="0"
                          value={l.discount_amount}
                          onChange={(e) => setLine(i, { discount_amount: e.target.value })}
                          className="text-right"
                        />
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-semibold">
                        {fmtUSD(lineTotal)}
                      </TableCell>
                      <TableCell>
                        {form.line_items.length > 1 && (
                          <Button
                            type="button" size="icon" variant="ghost"
                            onClick={() => setForm((f) => ({ ...f, line_items: f.line_items.filter((_, j) => j !== i) }))}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <div className="ml-auto max-w-sm space-y-1 text-sm">
            <Row label="Subtotal" value={totals.sub} />
            <Row label="Tax" value={totals.tax} />
            <Row label="Discount" value={-totals.disc} />
            <div className="border-t border-slate-200 pt-2 font-bold text-lg flex justify-between">
              <span>Total</span>
              <span className="tabular-nums text-[#2F86B5]" data-testid="inv-form-total">{fmtUSD(totals.tot)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate("/invoices")} disabled={busy}>Cancel</Button>
        <Button onClick={save} disabled={busy} className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white" data-testid="inv-form-save">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {isEdit ? "Save changes" : "Save draft"}
        </Button>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between">
      <span className="text-slate-600">{label}</span>
      <span className="tabular-nums">{fmtUSD(value)}</span>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Detail page
// ----------------------------------------------------------------------------

export function InvoiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [payDlg, setPayDlg] = useState(false);
  const [payForm, setPayForm] = useState({
    payment_date: new Date().toISOString().slice(0, 10),
    amount: "", payment_method: "bank_transfer",
    bank_account_id: "", reference_no: "", notes: "",
  });
  const [banks, setBanks] = useState([]);

  const canWrite = WRITE_ROLES.has(user?.role);
  const canApprove = APPROVE_ROLES.has(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/invoices/${id}`);
      setInv(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => { api.get("/bank-accounts").then(({ data }) => setBanks(data || [])); }, []);

  const act = async (path, body) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${id}/${path}`, body || {});
      setInv(data);
      toast.success("Done.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const recordPayment = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${id}/payments`, {
        ...payForm, amount: Number(payForm.amount),
      });
      setInv(data);
      setPayDlg(false);
      setPayForm((p) => ({ ...p, amount: "", reference_no: "", notes: "" }));
      toast.success("Payment recorded.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const duplicate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${id}/duplicate`);
      toast.success(`Duplicated → ${data.invoice_number}`);
      navigate(`/invoices/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  if (loading || !inv) {
    return <div className="flex justify-center py-10"><Loader2 className="animate-spin h-6 w-6 text-[#4FA3D1]" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="invoice-detail-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wide">
            {inv.invoice_type} invoice
          </div>
          <h2 className="text-2xl font-bold">{inv.invoice_number}</h2>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <Badge className={INV_STATUS_STYLE[inv.invoice_status]} data-testid="inv-status">
              {(inv.invoice_status || "draft").replace("_"," ")}
            </Badge>
            <Badge className={PAY_STATUS_STYLE[inv.payment_status]} data-testid="inv-payment-status">
              {(inv.payment_status || "unpaid").replace("_"," ")}
            </Badge>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {canWrite && (inv.invoice_status === "draft" || inv.invoice_status === "submitted" || user?.role === ROLES.SYSTEM_ADMIN || user?.full_access === true) && (
            <Button variant="outline" onClick={() => navigate(`/invoices/${id}/edit`)} data-testid="inv-edit-btn">
              <Pencil className="h-4 w-4 mr-1" /> Edit
            </Button>
          )}
          {canWrite && inv.invoice_status === "draft" && (
            <Button onClick={() => act("submit")} disabled={busy} data-testid="inv-submit-btn">
              <Send className="h-4 w-4 mr-1" /> Submit
            </Button>
          )}
          {canApprove && inv.invoice_status === "pending_approval" && (
            <>
              <Button
                onClick={() => act("approve")}
                disabled={busy}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="inv-approve-btn"
              >
                <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  const r = window.prompt("Reject reason?");
                  if (r !== null) act("reject", { reason: r });
                }}
                disabled={busy}
                className="text-red-600"
                data-testid="inv-reject-btn"
              >
                <XCircle className="h-4 w-4 mr-1" /> Reject
              </Button>
            </>
          )}
          {canWrite && inv.invoice_status === "approved"
            && (inv.payment_status === "unpaid" || inv.payment_status === "partially_paid" || inv.payment_status === "overdue") && (
            <Button onClick={() => setPayDlg(true)} className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white" data-testid="inv-payment-btn">
              <Banknote className="h-4 w-4 mr-1" /> Record payment
            </Button>
          )}
          {canApprove && inv.invoice_status === "approved" && inv.payment_status !== "cancelled" && (
            <Button
              variant="outline"
              onClick={() => {
                const r = window.prompt("Cancellation reason?");
                if (r !== null) act("cancel", { reason: r });
              }}
              disabled={busy}
              className="text-red-600"
              data-testid="inv-cancel-btn"
            >
              Cancel invoice
            </Button>
          )}
          {canWrite && (
            <Button variant="outline" onClick={duplicate} disabled={busy} data-testid="inv-duplicate-btn">
              <Copy className="h-4 w-4 mr-1" /> Duplicate
            </Button>
          )}
          <Button variant="outline" onClick={() => handlePreview(inv)} data-testid="inv-pdf-preview-btn">
            <ExternalLink className="h-4 w-4 mr-1" /> Preview
          </Button>
          <Button variant="outline" onClick={() => handleDownload(inv)} data-testid="inv-pdf-btn">
            <Download className="h-4 w-4 mr-1" /> PDF
          </Button>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle>Line items</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Unit Price</TableHead>
                  <TableHead className="text-right">Tax</TableHead>
                  <TableHead className="text-right">Discount</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(inv.line_items || []).map((l, i) => (
                  <TableRow key={i}>
                    <TableCell>{l.description}</TableCell>
                    <TableCell className="text-right tabular-nums">{l.quantity}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtUSD(l.unit_price)}</TableCell>
                    <TableCell className="text-right tabular-nums">{l.tax_rate}%</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtUSD(l.discount_amount)}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{fmtUSD(l.line_total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Summary</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <Row label="Subtotal" value={inv.subtotal} />
            <Row label="Tax" value={inv.tax_total} />
            <Row label="Discount" value={-inv.discount_total} />
            <div className="flex justify-between text-lg font-bold border-t border-slate-200 pt-2">
              <span>Total</span>
              <span className="tabular-nums text-[#2F86B5]">{fmtUSD(inv.total_amount)}</span>
            </div>
            <Row label="Paid" value={inv.paid_amount} />
            <div className="flex justify-between text-base font-semibold">
              <span>Balance due</span>
              <span className="tabular-nums" data-testid="inv-balance-due">{fmtUSD(inv.balance_due)}</span>
            </div>
            <div className="text-xs text-slate-500 pt-3 space-y-0.5">
              <div>Party: <span className="font-medium text-slate-700">{inv.party_name}</span></div>
              <div>Invoice date: {inv.invoice_date}</div>
              <div>Due: {inv.due_date || "—"}</div>
              {inv.linked_transaction_id && (
                <div>
                  Linked tx:{" "}
                  <Link to={`/transactions/${inv.linked_transaction_id}`} className="text-[#2F86B5] hover:underline">
                    open
                  </Link>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {(inv.payments || []).length > 0 && (
        <Card>
          <CardHeader><CardTitle>Payments ({inv.payments.length})</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inv.payments.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>{p.payment_date}</TableCell>
                    <TableCell>{p.payment_method}</TableCell>
                    <TableCell className="font-mono text-xs">{p.reference_no || "—"}</TableCell>
                    <TableCell>{p.notes || "—"}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{fmtUSD(p.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Dialog open={payDlg} onOpenChange={setPayDlg}>
        <DialogContent data-testid="inv-payment-dialog">
          <DialogHeader><DialogTitle>Record payment</DialogTitle></DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label>Date *</Label>
              <Input type="date" value={payForm.payment_date}
                onChange={(e) => setPayForm((p) => ({ ...p, payment_date: e.target.value }))}
                data-testid="pay-date" />
            </div>
            <div><Label>Amount (USD) *</Label>
              <Input type="number" step="0.01" min="0" value={payForm.amount}
                onChange={(e) => setPayForm((p) => ({ ...p, amount: e.target.value }))}
                data-testid="pay-amount" />
              <div className="text-xs text-slate-500 mt-1">Balance due: {fmtUSD(inv.balance_due)}</div>
            </div>
            <div><Label>Method</Label>
              <Select value={payForm.payment_method}
                onValueChange={(v) => setPayForm((p) => ({ ...p, payment_method: v }))}>
                <SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="mobile_money">Mobile money</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {payForm.payment_method !== "cash" && (
              <div><Label>Bank account</Label>
                <Select value={payForm.bank_account_id}
                  onValueChange={(v) => setPayForm((p) => ({ ...p, bank_account_id: v }))}>
                  <SelectTrigger data-testid="pay-bank"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {banks.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.bank_name} · {b.account_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="sm:col-span-2"><Label>Reference no.</Label>
              <Input value={payForm.reference_no}
                onChange={(e) => setPayForm((p) => ({ ...p, reference_no: e.target.value }))} />
            </div>
            <div className="sm:col-span-2"><Label>Notes</Label>
              <Textarea rows={2} value={payForm.notes}
                onChange={(e) => setPayForm((p) => ({ ...p, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayDlg(false)} disabled={busy}>Cancel</Button>
            <Button onClick={recordPayment} disabled={busy} className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white" data-testid="pay-save">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
